if (!customElements.get('jersey-customization')) {
  customElements.define(
    'jersey-customization',
    class JerseyCustomization extends HTMLElement {
      connectedCallback() {
        this.form = this.closest('form[data-type="add-to-cart-form"]');
        this.toggle = this.querySelector('[data-customization-toggle]');
        this.fields = this.querySelector('[data-customization-fields]');
        this.numberInput = this.querySelector('[data-jersey-number]');
        this.nameInput = this.querySelector('[data-player-name]');
        this.error = this.querySelector('[data-customization-error]');
        this.submitButton = this.form?.querySelector('button[type="submit"][name="add"]');
        this.dynamicCheckout = this.form?.querySelector('.shopify-payment-button');

        if (!this.form || !this.toggle || this.dataset.ready === 'true') return;

        this.dataset.ready = 'true';
        this.toggle.addEventListener('change', () => this.onToggle());
        this.numberInput?.addEventListener('input', () => this.clearError());
        this.nameInput?.addEventListener('blur', () => {
          this.nameInput.value = this.nameInput.value.trim();
        });
        this.form.addEventListener('submit', (event) => this.onSubmit(event), true);
      }

      onToggle() {
        const expanded = this.toggle.checked;
        this.fields.hidden = !expanded;
        this.fields.setAttribute('aria-hidden', String(!expanded));
        this.toggle.setAttribute('aria-expanded', String(expanded));
        if (this.dynamicCheckout) this.dynamicCheckout.hidden = expanded;
        this.clearError();

        if (expanded) {
          this.numberInput?.focus();
        } else {
          if (this.numberInput) this.numberInput.value = '';
          if (this.nameInput) this.nameInput.value = '';
          this.numberInput?.removeAttribute('aria-invalid');
        }
      }

      validateNumber() {
        const rawValue = this.numberInput?.value.trim() || '';
        const value = Number(rawValue);
        return rawValue !== '' && /^\d{1,2}$/.test(rawValue) && Number.isInteger(value) && value >= 0 && value <= 99;
      }

      showError(message) {
        if (!this.error) return;
        this.error.textContent = message;
        this.error.hidden = false;
        this.numberInput?.setAttribute('aria-invalid', 'true');
        this.numberInput?.focus();
      }

      clearError() {
        if (this.error) {
          this.error.textContent = '';
          this.error.hidden = true;
        }
        this.numberInput?.removeAttribute('aria-invalid');
      }

      setBusy(busy) {
        if (!this.submitButton) return;
        this.submitButton.toggleAttribute('disabled', busy);
        this.submitButton.setAttribute('aria-busy', String(busy));
        this.submitButton.classList.toggle('loading', busy);
        this.submitButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !busy);
      }

      createGroupId() {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
        return `samba-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      }

      async onSubmit(event) {
        if (!this.toggle.checked) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        if (this.dataset.addonAvailable !== 'true' || !Number(this.dataset.addonVariantId)) {
          this.showError(this.dataset.errorUnavailable);
          return;
        }

        if (!this.validateNumber()) {
          this.showError(this.dataset.errorInvalid);
          return;
        }

        if (this.submitButton?.getAttribute('aria-busy') === 'true') return;

        this.clearError();
        this.nameInput.value = this.nameInput.value.trim();

        const uniformVariantId = Number(this.form.querySelector('[name="id"]')?.value);
        const quantityInput =
          this.form.querySelector('[name="quantity"]') ||
          document.querySelector(`[name="quantity"][form="${CSS.escape(this.form.id)}"]`);
        const quantity = Math.max(1, Number(quantityInput?.value || 1));
        const jerseyNumber = this.numberInput.value.trim();
        const playerName = this.nameInput.value;
        const groupId = this.createGroupId();
        let uniformVariantTitle = '';
        try {
          const variants = JSON.parse(this.dataset.productVariants || '[]');
          uniformVariantTitle = variants.find((variant) => Number(variant.id) === uniformVariantId)?.title || '';
        } catch (error) {
          console.warn('Unable to read the selected uniform variant title:', error);
        }
        const uniformProperties = {
          'Jersey Number': jerseyNumber,
          '_customization_group_id': groupId,
          '_customization_role': 'uniform',
        };
        const addonProperties = {
          'Jersey Number': jerseyNumber,
          'Customized Product': this.dataset.productTitle,
          'Parent Product': this.dataset.productTitle,
          '_customization_group_id': groupId,
          '_customization_role': 'addon',
          '_parent_variant_id': String(uniformVariantId),
        };

        if (uniformVariantTitle && uniformVariantTitle !== 'Default Title') {
          addonProperties['Uniform Variant'] = uniformVariantTitle;
        }

        if (playerName) {
          uniformProperties['Player Name'] = playerName;
          addonProperties['Player Name'] = playerName;
        }

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const payload = {
          items: [
            { id: uniformVariantId, quantity, properties: uniformProperties },
            { id: Number(this.dataset.addonVariantId), quantity, properties: addonProperties },
          ],
        };

        if (cart) {
          payload.sections = cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
          cart.setActiveElement?.(document.activeElement);
        }

        this.setBusy(true);

        try {
          const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
          });
          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || this.dataset.errorUnavailable);
          }

          if (cart) {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'jersey-customization',
              productVariantId: uniformVariantId,
              cartData: result,
            });
            cart.renderContents(result);
          } else {
            window.location.href = window.routes.cart_url;
          }
        } catch (error) {
          console.error('Jersey customization add failed:', error);
          this.showError(error.message || this.dataset.errorUnavailable);
        } finally {
          this.setBusy(false);
        }
      }
    }
  );
}
